
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { calculateMonthlyProgress } from './utils/chartUtils';

interface MonthlyProgressProps {
  dailyTarget: number;
}

const MonthlyProgress: React.FC<MonthlyProgressProps> = ({ dailyTarget }) => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMonthlyProgress = async () => {
      setLoading(true);
      try {
        const progress = await calculateMonthlyProgress(user?.id || null, currentDate, dailyTarget);
        setMonthlyData(progress);
      } catch (error) {
        console.error('Error calculating monthly progress:', error);
        setMonthlyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyProgress();
  }, [user?.id, currentDate, dailyTarget]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = monthlyData.find(d => d.day === day);
      days.push({
        day,
        achieved: dayData?.achieved || false,
        isFuture: new Date(year, month, day) > new Date(),
        isToday: new Date(year, month, day).toDateString() === new Date().toDateString()
      });
    }

    return days;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
          Monthly progress
        </h4>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <div className="animate-pulse">
            <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const days = getDaysInMonth();

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
        Monthly progress
      </h4>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          
          <h5 className="text-base font-medium text-gray-800 dark:text-gray-200">
            {monthName}
          </h5>
          
          <button
            onClick={() => navigateMonth('next')}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          >
            <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayData, index) => (
            <div
              key={index}
              className={`
                aspect-square flex items-center justify-center text-sm rounded
                ${dayData === null ? '' : 
                  dayData.isFuture ? 'text-gray-300 dark:text-gray-600' :
                  dayData.isToday ? 'bg-primary/10 text-primary font-medium' :
                  'text-gray-700 dark:text-gray-300'
                }
              `}
            >
              {dayData === null ? '' : (
                <div className="relative w-full h-full flex items-center justify-center">
                  <span className="text-xs">{dayData.day}</span>
                  {dayData.achieved && !dayData.isFuture && (
                    <div className="absolute top-0 right-0">
                      <Check size={10} className="text-green-500" />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MonthlyProgress;
